import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button, Select, SelectItem} from '@heroui/react';
import {ReactComponent as Fil} from '../../assets/icons/filter.svg';
import DateInput from './Date';
import {DateObject} from 'react-multi-date-picker';
import {parseGregorianDateFilter} from '../../lib/formatJalaliDate';

const filterSelectClassNames = {
    trigger: 'border-[#e8ddd4] bg-white min-h-12 shadow-none',
    label: 'text-[#5c4a3d] font-semibold',
    value: 'text-[#2d2118]',
    popoverContent:
        'rest-filter-select-popover p-1 bg-white border border-[#e8ddd4] shadow-[0_16px_40px_rgba(45,33,24,0.18)] rounded-xl',
    listbox: 'bg-white p-0',
    listboxWrapper: 'bg-white max-h-60',
};

const filterSelectPopoverProps = {
    placement: 'bottom-start',
    offset: 6,
    shouldBlockScroll: true,
    classNames: {
        base: 'rest-filter-select-popover z-[11050]',
        content:
            'rest-filter-select-popover bg-white border border-[#e8ddd4] shadow-[0_16px_40px_rgba(45,33,24,0.18)] rounded-xl p-1',
    },
};

const filterSelectItemClasses = {
    base: [
        'rounded-lg px-3 py-2.5 my-0.5',
        'data-[hover=true]:bg-[#fff4ef]',
        'data-[selectable=true]:focus:bg-[#fff4ef]',
        'data-[selected=true]:bg-[#fff0e8]',
        'data-[selected=true]:text-[#c2410c]',
    ].join(' '),
    title: 'text-sm font-semibold text-[#2d2118]',
    selectedIcon: 'text-[#fd5208]',
};

const ModalFilter = ({filters, setFilters}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [fromDateValue, setFromDateValue] = useState(null);
    const [toDateValue, setToDateValue] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(filters.status || '');
    const [selectedPayment, setSelectedPayment] = useState(filters.payment_method || '');

    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setSelectedStatus(filters.status || '');
            setSelectedPayment(filters.payment_method || '');
            setFromDateValue(parseGregorianDateFilter(filters.from_date));
            setToDateValue(parseGregorianDateFilter(filters.to_date));
        }
    }, [isOpen, filters.status, filters.payment_method, filters.from_date, filters.to_date]);

    const handlerSubmit = (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = Object.fromEntries(form.entries());

        const newFilters = {};
        if (data.user_name) newFilters.user_name = data.user_name;
        if (data.mobile) newFilters.mobile = data.mobile;
        if (selectedStatus) newFilters.status = selectedStatus;
        if (selectedPayment) newFilters.payment_method = selectedPayment;
        if (fromDateValue) {
            newFilters.from_date = new DateObject(fromDateValue?.toDate()).format('YYYY-MM-DD');
        }
        if (toDateValue) {
            newFilters.to_date = new DateObject(toDateValue?.toDate()).format('YYYY-MM-DD');
        }

        setFilters(newFilters);
        close();
    };

    return (
        <>
            <button
                type="button"
                className="rest-panel-filter-btn"
                onClick={open}
                aria-label="فیلتر سفارش‌ها"
                aria-expanded={isOpen}
            >
                <Fil className="fill-gray-500"/>
            </button>

            {isOpen && createPortal(
                <div className="rest-filter-modal" role="dialog" aria-modal="true" aria-label="فیلتر سفارش‌ها">
                    <button
                        type="button"
                        className="rest-filter-modal__backdrop"
                        onClick={close}
                        aria-label="بستن فیلتر"
                    />

                    <div className="rest-filter-modal__sheet">
                        <div className="rest-filter-modal__accent" aria-hidden="true"/>

                        <form onSubmit={handlerSubmit} className="rest-filter-modal__form">
                            <h2 className="rest-filter-modal__title">فیلتر بر اساس</h2>

                            <div className="rest-filter-modal__fields">
                                <DateInput value={fromDateValue} setValue={setFromDateValue} label="از تاریخ"/>
                                <DateInput value={toDateValue} setValue={setToDateValue} label="تا تاریخ"/>

                                <div className="rest-filter-date">
                                    <label className="rest-filter-date__label" htmlFor="rest-filter-user-name">
                                        نام مشتری
                                    </label>
                                    <input
                                        id="rest-filter-user-name"
                                        className="rest-filter-date__input"
                                        defaultValue={filters.user_name || ''}
                                        name="user_name"
                                        type="text"
                                        autoComplete="off"
                                        placeholder="مثال: علی محمدی"
                                    />
                                </div>

                                <div className="rest-filter-date">
                                    <label className="rest-filter-date__label" htmlFor="rest-filter-mobile">
                                        شماره همراه مشتری
                                    </label>
                                    <input
                                        id="rest-filter-mobile"
                                        className="rest-filter-date__input"
                                        defaultValue={filters.mobile || ''}
                                        name="mobile"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel"
                                        placeholder="09xxxxxxxxx"
                                        dir="ltr"
                                    />
                                </div>

                                <Select
                                    name="status"
                                    label="وضعیت سفارش"
                                    labelPlacement="outside"
                                    placeholder="وضعیت سفارش"
                                    selectedKeys={selectedStatus ? [selectedStatus] : []}
                                    onSelectionChange={(keys) => setSelectedStatus(keys.currentKey || '')}
                                    variant="bordered"
                                    radius="lg"
                                    classNames={filterSelectClassNames}
                                    popoverProps={filterSelectPopoverProps}
                                    listboxProps={{itemClasses: filterSelectItemClasses}}
                                    scrollShadow={false}
                                >
                                    <SelectItem key="pending">در انتظار تایید</SelectItem>
                                    <SelectItem key="processing">در آماده سازی</SelectItem>
                                    <SelectItem key="completed">در انتظار پیک</SelectItem>
                                    <SelectItem key="delivery">تحویل شده</SelectItem>
                                    <SelectItem key="cancelled">کنسل شده</SelectItem>
                                </Select>

                                <Select
                                    name="payment_method"
                                    label="نحوه پرداخت"
                                    labelPlacement="outside"
                                    placeholder="نحوه پرداخت"
                                    selectedKeys={selectedPayment ? [selectedPayment] : []}
                                    onSelectionChange={(keys) => setSelectedPayment(keys.currentKey || '')}
                                    variant="bordered"
                                    radius="lg"
                                    classNames={filterSelectClassNames}
                                    popoverProps={filterSelectPopoverProps}
                                    listboxProps={{itemClasses: filterSelectItemClasses}}
                                    scrollShadow={false}
                                >
                                    <SelectItem key="cash">پرداخت در محل</SelectItem>
                                    <SelectItem key="online">پرداخت آنلاین</SelectItem>
                                </Select>
                            </div>

                            <div className="rest-filter-modal__actions">
                                <Button
                                    type="button"
                                    onPress={close}
                                    className="rest-filter-modal__btn rest-filter-modal__btn--ghost"
                                    radius="lg"
                                >
                                    انصراف
                                </Button>
                                <Button
                                    type="submit"
                                    className="rest-filter-modal__btn rest-filter-modal__btn--primary"
                                    radius="lg"
                                >
                                    تایید
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ModalFilter;
